// Enemy projectiles (mortar shots). Pooled; they move, expire, and damage the player
// (respecting i-frames). They don't collide with enemies, so no spatial hash is needed
// - there's only one target (the player).

import { Pool } from '../core/pool';
import type { SpatialHash } from '../core/spatialHash';
import { ARENA, ENEMY_SHOT, PLAYER } from './balance';
import { hitsBoulder } from './boulders';
import type { Boulder } from './boulders';
import type { PlayerState } from './world';

export interface EnemyShot {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  radius: number;
  damage: number;
  lifeSec: number;
  color: number;
  bouncesLeft: number;
  stasisSec: number;
  sprite: string; // texture key (default 'shot'; hidden Spor Sayer uses 'note')
}

const makeShot = (): EnemyShot => ({
  x: 0, y: 0, prevX: 0, prevY: 0, vx: 0, vy: 0, radius: ENEMY_SHOT.radius, damage: 0, lifeSec: 0, color: ENEMY_SHOT.color, bouncesLeft: 0, stasisSec: 0, sprite: 'shot',
});

export const createEnemyShotPool = (): Pool<EnemyShot> => new Pool(ENEMY_SHOT.poolCap, makeShot);

/** Fire a shot from (x,y) toward (tx,ty). `sprite`/`color` let callers vary the look (e.g.
 *  the hidden Spor Sayer's musical notes). */
export interface EnemyShotOptions {
  readonly damage?: number;
  readonly speed?: number;
  readonly radius?: number;
  readonly lifeSec?: number;
  readonly bounces?: number;
}

export function spawnEnemyShot(
  pool: Pool<EnemyShot>,
  x: number,
  y: number,
  tx: number,
  ty: number,
  sprite = 'shot',
  color: number = ENEMY_SHOT.color,
  options: EnemyShotOptions = {},
): void {
  const s = pool.acquire();
  if (s === null) return;
  const dx = tx - x;
  const dy = ty - y;
  const d = Math.sqrt(dx * dx + dy * dy) || 1;
  s.x = x;
  s.y = y;
  s.prevX = x;
  s.prevY = y;
  const speed = options.speed ?? ENEMY_SHOT.speed;
  s.vx = (dx / d) * speed;
  s.vy = (dy / d) * speed;
  s.radius = options.radius ?? ENEMY_SHOT.radius;
  s.damage = options.damage ?? ENEMY_SHOT.damage;
  s.lifeSec = options.lifeSec ?? ENEMY_SHOT.lifeSec;
  s.color = color;
  s.bouncesLeft = options.bounces ?? 0;
  s.stasisSec = 0;
  s.sprite = sprite;
}

/** Move shots, cull, and damage the player on contact. Returns true if the player was hit. */
export function updateEnemyShots(
  pool: Pool<EnemyShot>,
  dt: number,
  player: PlayerState,
  boulders: Boulder[],
  boulderHash: SpatialHash,
  boulderBuf: number[],
  vineWalls: Boulder[],
  vineHash: SpatialHash,
  vineBuf: number[],
  invuln: boolean,
  dmgMul: number,
): boolean {
  let hit = false;
  for (let i = 0; i < pool.count; i++) {
    const s = pool.items[i];
    s.prevX = s.x;
    s.prevY = s.y;
    if (s.stasisSec > 0) {
      s.stasisSec = Math.max(0, s.stasisSec - dt);
      s.lifeSec -= dt;
      continue;
    }
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.lifeSec -= dt;

    if (s.lifeSec <= 0) {
      pool.releaseAt(i);
      i--;
      continue;
    }
    if (s.x < 0 || s.y < 0 || s.x > ARENA.width || s.y > ARENA.height) {
      if (s.bouncesLeft > 0) {
        if (s.x <= 0 || s.x >= ARENA.width) s.vx = -s.vx;
        if (s.y <= 0 || s.y >= ARENA.height) s.vy = -s.vy;
        s.x = Math.max(0, Math.min(ARENA.width, s.x));
        s.y = Math.max(0, Math.min(ARENA.height, s.y));
        s.bouncesLeft--;
      } else {
        pool.releaseAt(i);
        i--;
        continue;
      }
    }
    if (hitsBoulder(s.x, s.y, s.radius, boulders, boulderHash, boulderBuf) || hitsBoulder(s.x, s.y, s.radius, vineWalls, vineHash, vineBuf)) {
      pool.releaseAt(i);
      i--;
      continue;
    }

    const dx = s.x - player.x;
    const dy = s.y - player.y;
    const rr = s.radius + player.radius;
    if (dx * dx + dy * dy <= rr * rr) {
      if (!invuln && player.iFrameSec <= 0) {
        player.hp -= s.damage * dmgMul;
        player.iFrameSec = PLAYER.iFramesSec;
        hit = true;
      }
      pool.releaseAt(i);
      i--;
    }
  }
  return hit;
}
